import { useEffect, useRef, useState } from 'react';
import { useParams } from 'wouter';
import { useQuery, useMutation } from '@tanstack/react-query';
import { SignaturePadField, type SignaturePadFieldHandle } from '@/components/SignaturePadField';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AlertCircle, Check, FileCheck, Pen, Save, Trash2 } from 'lucide-react';

interface CompletionOfWorksRecord {
  id: number;
  customerId: number | null;
  installationId: number | null;
  customerName: string | null;
  customerAddress: string | null;
  projectRefNumber: string | null;
  installationAddress: string | null;
  installationDescription: string | null;
  confirmationBuildingRegs: boolean;
  manufacturersManuals: boolean;
  serviceRequirements: boolean;
  maintenanceRequirements: boolean;
  performanceEstimates: boolean;
  mcsCertificate: boolean;
  mcsComplianceCertificate: boolean;
  customerComments: string | null;
  signatureImage: string | null;
  status: string | null;
  accessToken: string | null;
  dateOfSigning: string | null;
  sentAt: string | null;
  customerEmail: string | null;
  customerPhone: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export default function CompletionSignPage() {
  const { token } = useParams<{ token: string }>();
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);
  const signatureRef = useRef<SignaturePadFieldHandle>(null);
  const [signatureImage, setSignatureImage] = useState<string>('');
  const [signatureError, setSignatureError] = useState<string | null>(null);

  const readSignatureImage = () => {
    if (!signatureRef.current || signatureRef.current.isEmpty()) {
      setSignatureError('Please provide a signature');
      return null;
    }

    const dataURL = signatureRef.current.toDataURL('image/png');
    setSignatureImage(dataURL);
    setSignatureError(null);
    return dataURL;
  };

  // Form state for customer checkboxes and comments
  const [formState, setFormState] = useState({
    confirmationBuildingRegs: false,
    manufacturersManuals: false,
    serviceRequirements: false,
    maintenanceRequirements: false,
    performanceEstimates: false,
    mcsCertificate: false,
    mcsComplianceCertificate: false,
    customerComments: '',
  });

  // Comments removed per request

  // Fetch Completion of Works Record by token
  const {
    data: record,
    isLoading,
    isError,
  } = useQuery<CompletionOfWorksRecord>({
    queryKey: ['/api/public/completion-of-works/token', token],
    queryFn: async () => {
      try {
        const response = await fetch(`/api/public/completion-of-works/token/${token}`);

        if (!response.ok) {
          let message = response.statusText || 'Unable to load the Completion of Works Record';
          try {
            const errorData = await response.json();
            message = errorData.error || errorData.message || message;
          } catch {
            // Non-JSON edge/proxy response; keep the HTTP status visible to the customer.
          }
          throw new Error(`${message} (HTTP ${response.status})`);
        }

        return await response.json();
      } catch (error) {
        console.error('Error fetching completion record:', error);
        setError(
          'Unable to load the Completion of Works Record. The link may be invalid or expired.'
        );
        throw error;
      }
    },
    retry: 1,
  });

  // Sign Completion of Works Record mutation
  const signRecordMutation = useMutation({
    mutationFn: async (data: {
      signature: string;
      checkboxes: typeof formState;
      comments: string; // Keep parameter in API for backward compatibility
    }) => {
      try {
        const response = await fetch(`/api/public/completion-of-works/${token}/sign`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(data),
        });

        if (!response.ok) {
          throw new Error(`API request failed with status ${response.status}`);
        }

        return await response.json();
      } catch (error) {
        console.error('Error signing completion record:', error);
        throw error;
      }
    },
    onSuccess: () => {
      setSuccess(true);
      // Reset form after successful submission
      if (signatureRef.current) {
        signatureRef.current.clear();
      }
      setSignatureImage('');
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : 'Please try again or contact support.';
      setError(`Failed to submit your signature: ${message}`);
      console.error('Error signing completion record:', error);
    },
  });

  // Set initial checkbox states from record when loaded
  useEffect(() => {
    if (record) {
      setFormState({
        confirmationBuildingRegs: record.confirmationBuildingRegs || false,
        manufacturersManuals: record.manufacturersManuals || false,
        serviceRequirements: record.serviceRequirements || false,
        maintenanceRequirements: record.maintenanceRequirements || false,
        performanceEstimates: record.performanceEstimates || false,
        mcsCertificate: record.mcsCertificate || false,
        mcsComplianceCertificate: record.mcsComplianceCertificate || false,
        customerComments: record.customerComments || '',
      });

      // Check if record is already signed
      if (record.status === 'signed') {
        setSuccess(true);
      }
    }
  }, [record]);

  // Handle signature actions
  const clearSignature = () => {
    if (signatureRef.current) {
      signatureRef.current.clear();
      setSignatureImage('');
      setSignatureError(null);
    }
  };

  const saveSignature = () => {
    readSignatureImage();
  };

  // Handle checkbox changes
  const handleCheckboxChange = (field: string) => (checked: boolean) => {
    setFormState((prev) => ({
      ...prev,
      [field]: checked,
    }));
  };

  // Handle submission
  const handleSubmit = () => {
    const imageToSubmit = readSignatureImage();
    if (!imageToSubmit) {
      setSignatureError('Please sign before submitting');
      return;
    }

    signRecordMutation.mutate({
      signature: imageToSubmit,
      checkboxes: formState,
      comments: formState.customerComments || '', // Use the customer comments from form state
    });
  };

  // Format dates for display
  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  if (isLoading) {
    return (
      <div className="container max-w-4xl mx-auto py-8 px-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col items-center justify-center py-10">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
              <p className="text-lg">Loading Completion of Works Record...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isError || error) {
    return (
      <div className="container max-w-4xl mx-auto py-8 px-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-destructive flex items-center gap-2">
              <AlertCircle className="h-5 w-5" />
              Error
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p>
              {error ||
                'There was an error loading the Completion of Works Record. The link may be invalid or expired.'}
            </p>
          </CardContent>
          <CardFooter>
            <p className="text-sm text-muted-foreground">
              If you continue to experience issues, please contact our support team.
            </p>
          </CardFooter>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="container max-w-4xl mx-auto py-8 px-4">
        <Card>
          <CardHeader className="bg-green-50 dark:bg-green-900/20 rounded-t-lg">
            <CardTitle className="text-green-700 dark:text-green-400 flex items-center gap-2">
              <Check className="h-5 w-5" />
              Completion of Works Record Signed
            </CardTitle>
            <CardDescription className="text-green-600 dark:text-green-500">
              Thank you for confirming the completion of your installation
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="space-y-6">
              <div>
                <h3 className="font-semibold mb-1">Installation Details</h3>
                <p>Project Reference: {record?.projectRefNumber}</p>
                <p>Customer: {record?.customerName}</p>
                <p>Installation Address: {record?.installationAddress}</p>
                <p>Date Signed: {formatDate(record?.dateOfSigning)}</p>
              </div>

              <div>
                <p className="text-center my-8">
                  A confirmation email has been sent to your email address.
                </p>

                <div className="text-center">
                  <Button className="gap-2" onClick={() => window.close()}>
                    <Check className="h-4 w-4" />
                    Close this page
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container max-w-4xl mx-auto py-8 px-4">
      <Card>
        <CardHeader className="pb-0">
          <div className="flex items-center justify-between mb-2">
            <img
              src="/domelec-logo-new-official.png"
              alt="DomElec Logo"
              className="h-12 object-contain"
              style={{ maxWidth: '200px' }}
            />
            <div className="hidden sm:block">
              <Badge variant="outline" className="ml-auto font-semibold">
                Completion of Works Record
              </Badge>
            </div>
          </div>
          <CardTitle className="flex items-center gap-2 mt-4">
            <FileCheck className="h-5 w-5 text-primary" />
            Completion of Works Record
          </CardTitle>
          <CardDescription>
            Please confirm receipt of documentation and sign to acknowledge completion of your
            installation
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            <div className="grid gap-6 md:grid-cols-2">
              <div>
                <h3 className="font-semibold mb-1">Installation Details</h3>
                <p>Project Reference: {record?.projectRefNumber}</p>
                <p>Customer: {record?.customerName}</p>
                <p>Installation Address: {record?.installationAddress}</p>
                <p>Installation Type: {record?.installationDescription}</p>
              </div>
              <div>
                <h3 className="font-semibold mb-1">Status</h3>
                <div className="flex items-center gap-2 mb-1">
                  <Badge variant="outline">
                    {record?.status === 'draft' ? 'Pending Signature' : record?.status}
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground">
                  Date Sent: {formatDate(record?.sentAt)}
                </p>
              </div>
            </div>

            <Separator />

            <div>
              <h3 className="font-semibold mb-4">Documentation Provided</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Please check the boxes below to confirm you have received and understand the
                following documentation:
              </p>

              <div className="grid gap-3 md:grid-cols-2 bg-muted/20 rounded-lg p-4">
                <div className="flex items-start space-x-3 p-2 rounded-lg transition-colors hover:bg-muted/40">
                  <Checkbox
                    id="confirmationBuildingRegs"
                    checked={formState.confirmationBuildingRegs}
                    onCheckedChange={handleCheckboxChange('confirmationBuildingRegs')}
                    className="mt-1"
                  />
                  <div>
                    <Label htmlFor="confirmationBuildingRegs" className="font-medium text-sm">
                      Building Regulations Compliance Certification
                    </Label>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      I confirm I have received this document
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3 p-2 rounded-lg transition-colors hover:bg-muted/40">
                  <Checkbox
                    id="manufacturersManuals"
                    checked={formState.manufacturersManuals}
                    onCheckedChange={handleCheckboxChange('manufacturersManuals')}
                    className="mt-1"
                  />
                  <div>
                    <Label htmlFor="manufacturersManuals" className="font-medium text-sm">
                      Manufacturer's Installation and Operating Manuals
                    </Label>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      I confirm I have received this document
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3 p-2 rounded-lg transition-colors hover:bg-muted/40">
                  <Checkbox
                    id="serviceRequirements"
                    checked={formState.serviceRequirements}
                    onCheckedChange={handleCheckboxChange('serviceRequirements')}
                    className="mt-1"
                  />
                  <div>
                    <Label htmlFor="serviceRequirements" className="font-medium text-sm">
                      Service Requirements
                    </Label>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      I confirm I have received this document
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3 p-2 rounded-lg transition-colors hover:bg-muted/40">
                  <Checkbox
                    id="maintenanceRequirements"
                    checked={formState.maintenanceRequirements}
                    onCheckedChange={handleCheckboxChange('maintenanceRequirements')}
                    className="mt-1"
                  />
                  <div>
                    <Label htmlFor="maintenanceRequirements" className="font-medium text-sm">
                      Maintenance Requirements
                    </Label>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      I confirm I have received this document
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3 p-2 rounded-lg transition-colors hover:bg-muted/40">
                  <Checkbox
                    id="performanceEstimates"
                    checked={formState.performanceEstimates}
                    onCheckedChange={handleCheckboxChange('performanceEstimates')}
                    className="mt-1"
                  />
                  <div>
                    <Label htmlFor="performanceEstimates" className="font-medium text-sm">
                      Performance and Financial Estimates
                    </Label>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      I confirm I have received this document
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3 p-2 rounded-lg transition-colors hover:bg-muted/40">
                  <Checkbox
                    id="mcsCertificate"
                    checked={formState.mcsCertificate}
                    onCheckedChange={handleCheckboxChange('mcsCertificate')}
                    className="mt-1"
                  />
                  <div>
                    <Label htmlFor="mcsCertificate" className="font-medium text-sm">
                      MCS or Flexi-Orb Certificate
                    </Label>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      I confirm I have received this document
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3 p-2 rounded-lg transition-colors hover:bg-muted/40">
                  <Checkbox
                    id="mcsComplianceCertificate"
                    checked={formState.mcsComplianceCertificate}
                    onCheckedChange={handleCheckboxChange('mcsComplianceCertificate')}
                    className="mt-1"
                  />
                  <div>
                    <Label htmlFor="mcsComplianceCertificate" className="font-medium text-sm">
                      MCS Compliance Certificate
                    </Label>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      I confirm I have received this document
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <Separator />

            <div>
              <h3 className="font-semibold mb-4">Additional Comments</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Please provide any additional comments or feedback about your installation
                (optional):
              </p>

              <Textarea
                placeholder="Your comments or feedback..."
                className="resize-none min-h-[100px]"
                value={formState.customerComments || ''}
                onChange={(e) =>
                  setFormState({
                    ...formState,
                    customerComments: e.target.value,
                  })
                }
              />
            </div>

            <Separator />

            <div>
              <h3 className="font-semibold mb-4">Signature</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Please sign below to confirm that the installation has been completed to your
                satisfaction and that you have received all the necessary documentation.
              </p>

              <div className="border border-input rounded-md p-2">
                <SignaturePadField
                  ref={signatureRef}
                  penColor="black"
                  className="signature-canvas w-full h-40"
                  style={{
                    background: 'white',
                    border: '1px solid #eee',
                    touchAction: 'none',
                    WebkitUserSelect: 'none',
                    userSelect: 'none',
                    WebkitTouchCallout: 'none',
                  }}
                />
              </div>

              {signatureError && <p className="text-sm text-destructive mt-2">{signatureError}</p>}

              <div className="flex gap-3 mt-4">
                <Button type="button" variant="outline" onClick={clearSignature} className="gap-2">
                  <Trash2 className="h-4 w-4" />
                  Clear Signature
                </Button>
                <Button type="button" onClick={saveSignature} className="gap-2">
                  <Save className="h-4 w-4" />
                  Save Signature
                </Button>
              </div>

              {signatureImage && (
                <div className="mt-4 p-4 rounded-md border bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 flex items-center gap-3">
                  <Check className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                  <p className="text-green-700 dark:text-green-400">
                    Signature saved successfully!
                  </p>
                </div>
              )}
            </div>
          </div>
        </CardContent>
        <CardFooter className="flex flex-col items-stretch gap-4">
          <Button
            className="w-full gap-2"
            size="lg"
            disabled={signRecordMutation.isPending}
            onClick={handleSubmit}
          >
            {signRecordMutation.isPending ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Processing...
              </>
            ) : (
              <>
                <Pen className="h-4 w-4" />
                Submit Signed Completion of Works Record
              </>
            )}
          </Button>

          <p className="text-sm text-muted-foreground text-center">
            Compliance Advice Guide © HIES 2022 | Completion of Works Record | Version 1 | Date of
            circulation: 17th October 2022
          </p>
          <p className="text-xs text-muted-foreground text-center">
            By signing this document, you confirm that all information provided is true and accurate
            to the best of your knowledge.
          </p>
        </CardFooter>
      </Card>
    </div>
  );
}
